// ════════════════════════════════════════════════════════════
//  FrontRow.jsx — the spectator's view switch + seat controls
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: sit in a real seat, or keep the broadcast that tracks the
//  fighters — your choice, switchable any time. Everything here writes into the
//  shared `userCam` ref (BattleCamera reads it every frame); React only repaints
//  the little label, never the fight.
//    V = switch view · R = eyes on the fight (front row) / camera default (broadcast)
//    ← → = along the row · ↑ ↓ = back / forward a tread (past the last, the top deck)
//  Spectators only: a fighter keeps the broadcast.
//  THE SKY BOX (caP'n 2026-10-17) is the third view: V goes Broadcast → Front Row → Sky Box.
//  In there your body walks the box (ui/SkyControls.jsx has its keys and buttons).
// ════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from "react";
import { loadPrefs, savePrefs, stepSeat, stepRow, seatLabelParts } from "../three/frontRow.js";
import { say } from "../data/text.js";
import HideBtn from "./HideBtn.jsx";
import SkyControls from "./SkyControls.jsx";
import { BOX, boxInside } from "../online/skybox.js";
import { skyCamDefault } from "../three/BattleCamera.jsx";

const VIEWS = ["broadcast", "front", "sky"];

const typing = (e) => { const t = e.target; return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable); };

export default function FrontRow({ userCam }) {
  const [view, setView] = useState(() => {
    const p = loadPrefs(); const uc = userCam.current;
    const v = BOX.wantView || p.view; BOX.wantView = null;   // "Sit with them" lands you straight in the box
    uc.view = v; uc.seat = p.seat; uc.free = false; uc.seatZoom = 1;
    return v;
  });
  // your BODY is in the Sky Box for the whole fight, whichever camera you look through (caP'n 2026-10-21: switching views changes the
  // view, it never moves you); only leaving the fight takes you out
  useEffect(() => { boxInside(true); return () => boxInside(false); }, [userCam]);
  const [seat, setSeat] = useState(() => userCam.current.seat);
  const [free, setFree] = useState(false);

  // the "eyes on the fight" button only matters once you've looked away — poll the ref lightly
  useEffect(() => { const id = setInterval(() => setFree(!!userCam.current.free), 250); return () => clearInterval(id); }, [userCam]);

  const pick = useCallback((v) => {
    const uc = userCam.current; uc.view = v; uc.free = false; setView(v); setFree(false); savePrefs(v, uc.seat);
    if (v === "sky") skyCamDefault(uc);   // look out through your own eyes, facing the fight (your body was already there)
  }, [userCam]);
  // the view can be switched from outside too (Lyraei's seat: "wait for partner" puts her in the Sky Box)
  useEffect(() => { BOX.ui.setView = pick; return () => { if (BOX.ui.setView === pick) BOX.ui.setView = null; }; }, [pick]);
  const move = useCallback((fn, arg) => { const uc = userCam.current; uc.seat = fn(uc.seat, arg); setSeat(uc.seat); savePrefs(uc.view, uc.seat); }, [userCam]);
  const eyes = useCallback(() => {
    const uc = userCam.current;
    if (uc.view === "sky") { if (BOX.me.seat >= 0) { uc.free = false; uc.seatZoom = 1; setFree(false); } else skyCamDefault(uc); }
    else if (uc.view === "front") { uc.free = false; uc.seatZoom = 1; setFree(false); }
    else { uc.yaw = 0; uc.pitch = 0; uc.zoom = 0; }
  }, [userCam]);

  useEffect(() => {
    const down = (e) => {
      if (typing(e) || e.ctrlKey || e.metaKey || e.altKey || BOX.ui.modal) return;
      const k = e.key.toLowerCase(), uc = userCam.current;
      if (k === "v") { if (!e.repeat) pick(VIEWS[(VIEWS.indexOf(uc.view) + 1) % VIEWS.length]); return; }
      if (k === "r") { if (!e.repeat) eyes(); return; }
      if (uc.view !== "front") return;
      if (k === "arrowleft") { e.preventDefault(); move(stepSeat, -1); }
      else if (k === "arrowright") { e.preventDefault(); move(stepSeat, 1); }
      else if (k === "arrowup") { e.preventDefault(); move(stepRow, 1); }
      else if (k === "arrowdown") { e.preventDefault(); move(stepRow, -1); }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [pick, move, eyes, userCam]);

  const lab = seatLabelParts(seat);
  return (
    <div className="fr-bar">
      <div className="fr-seg" role="group">
        <button className={view === "broadcast" ? "on" : ""} onClick={() => pick("broadcast")} title="V">{say("frontrow.view.broadcast")}</button>
        <button className={view === "front" ? "on" : ""} onClick={() => pick("front")} title="V">{say("frontrow.view.front")}</button>
        <button className={view === "sky" ? "on" : ""} onClick={() => pick("sky")} title="V">{say("frontrow.view.sky")}</button>
      </div>
      {view === "front" && (
        <div className="fr-seat">
          <button onClick={() => move(stepSeat, -1)} title="←">◀</button>
          <button onClick={() => move(stepSeat, 1)} title="→">▶</button>
          <button onClick={() => move(stepRow, 1)} title="↑">▲</button>
          <button onClick={() => move(stepRow, -1)} title="↓">▼</button>
          <span className="fr-label">{say("frontrow.seat", { deck: say("frontrow.deck." + lab.deck), row: lab.row, seat: lab.seat })}</span>
          {free && <button className="fr-eyes" onClick={eyes} title="R">{say("frontrow.eyes")}</button>}
        </div>
      )}
      {view === "front" && <div className="fr-hint">{say("frontrow.hint")}</div>}
      {view === "sky" && <SkyControls />}
      {view === "sky" && free && <button className="fr-eyes sky-eyes" onClick={eyes} title="R">{say("frontrow.eyes")}</button>}
      <HideBtn id="front" />
    </div>
  );
}
